import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';

const MONTHS_BEFORE = 1200;
const MONTHS_AFTER = 1200;
const OVERSCAN = 4; // 뷰포트 위아래로 미리 렌더링할 월 수
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getEventColor = (ev) => ev.backgroundColor || ev.color || '#6366f1';

const getEventTitle = (ev) => {
  const t = ev.title || ev.extendedProps?.title || '';
  return t === '가능' ? '선호' : t;
};

const getMonthGrid = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
};

// 월 높이 추정 (가상 스크롤용)
const estimateMonthHeight = (year, month, prevYear, monthIdx, cellMinH) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const rows = Math.ceil((firstDay + daysInMonth) / 7);
  const showYear = year !== prevYear || monthIdx === 0;
  const borderH = monthIdx > 0 ? 1 : 0;
  const headerH = borderH + 18 + (showYear ? 18 : 0) + 24; // padding + year? + month text
  const weekdayH = 23;
  const gridH = 12 + rows * cellMinH;
  return headerH + weekdayH + gridH;
};

const MultiMonthScrollView = ({ events = [], displayMode, onDateClick, onEventClick, selectedDate, onScroll, onVisibleMonthChange }) => {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(600);
  const didScrollToToday = useRef(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = toDateStr(today);
  const selectedStr = selectedDate
    ? toDateStr(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()))
    : null;

  const isDetail = displayMode === 'detail';
  const cellMinH = isDetail ? 64 : 52;
  const MAX_EVENTS = isDetail ? 2 : 3;

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      if (!ev.start) continue;
      const d = new Date(ev.start);
      if (isNaN(d)) continue;
      const ds = toDateStr(d);
      if (!map[ds]) map[ds] = [];
      map[ds].push(ev);
    }
    return map;
  }, [events]);

  // 전체 월 목록 (한 번만 계산)
  const months = useMemo(() => {
    const arr = [];
    for (let i = -MONTHS_BEFORE; i <= MONTHS_AFTER; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      arr.push({ year: d.getFullYear(), month: d.getMonth() });
    }
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 각 월 높이 및 누적 높이 (displayMode 바뀌면 재계산)
  const { cumHeights, totalHeight, todayOffset } = useMemo(() => {
    const cum = [0];
    let todayOff = 0;
    for (let i = 0; i < months.length; i++) {
      const { year, month } = months[i];
      const prevYear = i > 0 ? months[i - 1].year : year;
      const h = estimateMonthHeight(year, month, prevYear, i, cellMinH);
      cum.push(cum[i] + h);
      if (year === today.getFullYear() && month === today.getMonth()) {
        todayOff = cum[i];
      }
    }
    return { cumHeights: cum, totalHeight: cum[months.length], todayOffset: todayOff };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, cellMinH]);

  // 최초 마운트 시 오늘 월로 스크롤
  useEffect(() => {
    if (containerRef.current && !didScrollToToday.current) {
      containerRef.current.scrollTop = todayOffset;
      setScrollTop(todayOffset);
      didScrollToToday.current = true;
    }
  }, [todayOffset]);

  // displayMode 변경 시 현재 보이는 월 유지
  const prevCellMinH = useRef(cellMinH);
  useEffect(() => {
    if (prevCellMinH.current !== cellMinH && containerRef.current) {
      // scrollTop 비율로 새 위치 추정
      const ratio = containerRef.current.scrollTop / (prevCellMinH.current === 64
        ? totalHeight // detail heights
        : totalHeight);
      containerRef.current.scrollTop = todayOffset; // 간단히 오늘로
      setScrollTop(todayOffset);
    }
    prevCellMinH.current = cellMinH;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellMinH]);

  // 컨테이너 높이 측정
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setContainerHeight(e.contentRect.height);
    });
    ro.observe(containerRef.current);
    setContainerHeight(containerRef.current.clientHeight);
    return () => ro.disconnect();
  }, []);

  // 현재 스크롤 위치에서 렌더링할 월 범위
  const { startIdx, endIdx } = useMemo(() => {
    // 이진 탐색으로 첫 번째 보이는 월 찾기
    let lo = 0, hi = months.length - 1;
    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (cumHeights[mid + 1] <= scrollTop) lo = mid + 1;
      else hi = mid;
    }
    const start = Math.max(0, lo - OVERSCAN);
    let end = lo;
    while (end < months.length && cumHeights[end] < scrollTop + containerHeight) end++;
    end = Math.min(months.length - 1, end + OVERSCAN);
    return { startIdx: start, endIdx: end };
  }, [scrollTop, containerHeight, cumHeights, months.length]);

  const handleScroll = useCallback((e) => {
    const st = e.target.scrollTop;
    setScrollTop(st);

    // 뷰포트 중간 기준으로 현재 월 계산 → 헤더 업데이트
    if (onVisibleMonthChange) {
      const midPoint = st + containerHeight / 2;
      let lo = 0, hi = months.length - 1;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (cumHeights[mid + 1] <= midPoint) lo = mid + 1;
        else hi = mid;
      }
      onVisibleMonthChange(months[lo]);
    }

    onScroll && onScroll(e);
  }, [onScroll, onVisibleMonthChange, months, cumHeights]);

  const topSpacerH = cumHeights[startIdx];
  const bottomSpacerH = totalHeight - cumHeights[endIdx + 1];

  return (
    <div
      ref={containerRef}
      style={{ flex: 1, overflowY: 'auto', backgroundColor: '#fff', minHeight: 0 }}
      onScroll={handleScroll}
    >
      {/* 상단 빈 공간 (렌더링 안 된 월들) */}
      <div style={{ height: topSpacerH }} />

      {months.slice(startIdx, endIdx + 1).map(({ year, month }, relIdx) => {
        const monthIdx = startIdx + relIdx;
        const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();
        const grid = getMonthGrid(year, month);
        const prevYear = monthIdx > 0 ? months[monthIdx - 1].year : year;

        return (
          <div key={`${year}-${month}`}>
            {/* 월 헤더 */}
            <div style={{
              padding: '14px 14px 4px',
              borderTop: monthIdx > 0 ? '1px solid #e5e7eb' : 'none',
            }}>
              {(year !== prevYear || monthIdx === 0) && (
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, marginBottom: 1 }}>
                  {year}년
                </div>
              )}
              <div style={{
                fontSize: 22, fontWeight: 800,
                color: isCurrentMonth ? '#2563eb' : '#111827',
                lineHeight: 1.1,
              }}>
                {month + 1}월
              </div>
            </div>

            {/* 요일 헤더 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '6px 8px 2px' }}>
              {WEEKDAYS.map((day, i) => (
                <div key={i} style={{
                  textAlign: 'center', fontSize: 11, fontWeight: 600,
                  color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#9ca3af',
                }}>
                  {day}
                </div>
              ))}
            </div>

            {/* 날짜 그리드 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 8px 12px' }}>
              {grid.map((date, idx) => {
                if (date === null) {
                  return <div key={`empty-${idx}`} style={{ minHeight: cellMinH }} />;
                }

                const ds = toDateStr(date);
                const isToday = ds === todayStr;
                const isSelected = ds === selectedStr;
                const dayOfWeek = date.getDay();
                const isSun = dayOfWeek === 0;
                const isSat = dayOfWeek === 6;
                const dayEvents = eventsByDate[ds] || [];
                const extraCount = dayEvents.length - MAX_EVENTS;
                const numColor = isToday ? '#fff' : isSun ? '#ef4444' : isSat ? '#3b82f6' : '#111827';

                return (
                  <div
                    key={ds}
                    onClick={() => onDateClick && onDateClick({ date, dateStr: ds })}
                    style={{ minHeight: cellMinH, padding: '4px 2px 2px', cursor: 'pointer' }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      backgroundColor: isToday ? '#2563eb' : isSelected ? '#dbeafe' : 'transparent',
                      color: numColor,
                      fontSize: 14, fontWeight: isToday ? 700 : 400,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 3px',
                    }}>
                      {date.getDate()}
                    </div>

                    {isDetail ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {dayEvents.slice(0, MAX_EVENTS).map((ev, ei) => (
                          <div
                            key={ei}
                            title={getEventTitle(ev)}
                            style={{
                              backgroundColor: getEventColor(ev),
                              color: '#fff',
                              fontSize: 10, fontWeight: 500,
                              padding: '2px 4px', borderRadius: 3,
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', lineHeight: 1.4,
                              width: '100%', boxSizing: 'border-box',
                            }}
                          >
                            {getEventTitle(ev)}
                          </div>
                        ))}
                        {extraCount > 0 && (
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, textAlign: 'left', paddingLeft: 2, lineHeight: 1 }}>
                            +{extraCount} 더
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 1 }}>
                        {dayEvents.slice(0, MAX_EVENTS).map((ev, ei) => (
                          <div key={ei} style={{
                            height: 4, width: '100%',
                            backgroundColor: getEventColor(ev),
                            borderRadius: 2,
                          }} />
                        ))}
                        {extraCount > 0 && (
                          <div style={{ fontSize: 9, color: '#9ca3af', textAlign: 'center', lineHeight: 1 }}>
                            ···
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* 하단 빈 공간 (렌더링 안 된 월들) */}
      <div style={{ height: bottomSpacerH }} />
    </div>
  );
};

export default MultiMonthScrollView;
